// src/ui/overlays/HeadstartCountdown.tsx — P3 UI-03.
//
// Replaces the RoundTimer slot during round.phase === 'headstart' (first 20 s
// of every round). Counts down 20 → 1; final 3 seconds highlight the digit in
// accent amber and swap the sub-label copy from "Hiders hide. Hunters wait."
// to "Go!" — the locked copywriting variants from UI-SPEC §Copywriting.
//
// WARNING-15 tier-isolation: this component subscribes ONLY to the renderBus
// 'sim-tick' payload. Zero sim-tier imports.
//
// Source: Phase 3 UI-SPEC §HeadstartCountdown; shares CSS with RoundTimer
// (same slot, same chip shape).

import { useEffect, useState, type JSX } from 'react';

import { renderBus } from '../../render/bus';
import type { EntityRole } from '../../shared/types/world';

import './round-timer.css';

interface HeadstartState {
  phase: 'headstart' | 'active' | 'ended';
  tick: number;
  headstartEndTick: number;
  // P7 Plan 09 (UI-08, D-07): role reminder fold-in. Already on e.playerRole —
  // zero new sim plumbing.
  playerRole: EntityRole;
}

// P7 Plan 09 — role verdict copy + role-identity tint (07-UI-SPEC §Surface 5,
// §Copywriting). Green #5cb874 = hider, coral #e05c4a = hunter; both are
// existing palette values (success-green + destructive-coral), NOT new tokens,
// and the tint is always paired with the text label so colour is never the
// sole signal. dog/adult never appear during the head-start window so the
// verdict block is gated to hunter|hider only.
const ROLE_VERDICT: Record<
  'hider' | 'hunter',
  { verdict: string; subcopy: string; color: string }
> = {
  hider: {
    verdict: "You're a Hider",
    subcopy: "Don't get caught. Free your team.",
    color: '#5cb874',
  },
  hunter: {
    verdict: "You're a Hunter",
    subcopy: 'Light them up. Catch them all.',
    color: '#e05c4a',
  },
};

export function HeadstartCountdown(): JSX.Element | null {
  const [state, setState] = useState<HeadstartState | null>(null);

  useEffect(() => {
    return renderBus.on('sim-tick', (e) => {
      setState({
        phase: e.round.phase,
        tick: e.tick,
        headstartEndTick: e.round.headstartEndTick,
        playerRole: e.playerRole,
      });
    });
  }, []);

  if (!state || state.phase !== 'headstart') return null;

  // 30 Hz tick — ceil so the last partial second still reads as "1".
  const digit = Math.max(
    0,
    Math.ceil((state.headstartEndTick - state.tick) / 30),
  );
  const isFinal = digit > 0 && digit <= 3;
  const label = isFinal ? 'Go!' : 'Hiders hide. Hunters wait.';

  const role =
    state.playerRole === 'hunter' || state.playerRole === 'hider'
      ? state.playerRole
      : null;
  const verdict = role ? ROLE_VERDICT[role] : null;

  return (
    <div
      className="headstart-countdown"
      data-testid="headstart-countdown"
      data-final={isFinal ? 'true' : 'false'}
      role="timer"
      aria-live="polite"
      aria-label={`Head-start, ${digit} seconds remaining`}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {verdict && role && (
        <span
          className={`headstart-countdown__verdict headstart-countdown__verdict--${role}`}
          data-testid="headstart-role-verdict"
          aria-live="polite"
          // Inline tint mirrors round-timer.css so jsdom asserts the role
          // colour without loading the stylesheet (PATTERNS Pattern E).
          style={{ color: verdict.color }}
        >
          {verdict.verdict}
        </span>
      )}
      {verdict && (
        <span
          className="headstart-countdown__subcopy"
          data-testid="headstart-role-subcopy"
        >
          {verdict.subcopy}
        </span>
      )}
      <span className="headstart-countdown__digit">{digit}</span>
      <span className="headstart-countdown__label">{label}</span>
    </div>
  );
}
